'use client';

import { fetchData } from '@eliadmin/core/src/api/fetcher';
import { useEffect, useRef } from 'react';

type OfferViewTrackerProps = { offerId: number; locale: string; enabled: boolean };

/** Runs only after a visible detail is mounted, never during SSR or link prefetch. */
export function OfferViewTracker({ offerId, locale, enabled }: OfferViewTrackerProps) {
  const recordedOffer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !Number.isSafeInteger(offerId) || offerId <= 0) return;

    const record = () => {
      if (document.visibilityState !== 'visible' || recordedOffer.current === offerId) return;
      recordedOffer.current = offerId;
      const storageKey = `jobinterview:offer-view:${offerId}`;
      const timestamp = Date.now();
      try {
        const previous = Number(window.localStorage.getItem(storageKey));
        const elapsed = timestamp - previous;
        if (previous > 0 && elapsed >= 0 && elapsed < 60_000) return;
        window.localStorage.setItem(storageKey, String(timestamp));
      } catch {
        // Disabled storage must not block the page; the server also limits views.
      }
      const eventId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
              const random = Math.floor(Math.random() * 16);
              return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
            });
      void fetchData({
        method: 'POST',
        uri: '/store/interactions',
        locale,
        body: {
          event_id: eventId,
          target_type: 'job_portal_offer',
          target_id: String(offerId),
          action: 'view_offer',
          metadata: { placement: 'offer_detail' },
        },
      }).catch(() => {
        try {
          if (window.localStorage.getItem(storageKey) === String(timestamp))
            window.localStorage.removeItem(storageKey);
        } catch {
          // The storage may be unavailable in private or restricted browser modes.
        }
      });
    };

    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [offerId, locale, enabled]);

  return null;
}
